import React, { useState } from "react";
import { CommandBarButtonMenuItem, CommandBarButton, ListActionButtonProps, TooltipHost } from "sprint-common-web";

import { OrderRequestStatus } from "../../../../common/types/OrderRequestStatus";
import { AuthorizationWrapper } from "../../../../core/security/AuthorizationWrapper";
import { SprintAccessRight } from "../../../../core/security/SprintAccessRight";
import { ListOrderRequestErrorDetails } from "../error-details/ListOrderRequestErrorDetails";
import { MoveOrderRequestToPendingDialog } from "../move-to-pending/MoveOrderRequestToPendingDialog";
import { OrderRequestListingItem } from "../OrderRequestListingItem";
import { useRefreshOrderRequest } from "../refresh/useRefreshOrderRequest";
import { SendOrderRequestToCloudDialog } from "../sending/send-to-cloud/SendOrderRequestToCloudDialog";
import { SendOrderRequestToOnPremDialog } from "../sending/send-to-on-prem/SendOrderRequestToOnPremDialog";

enum OrderRequestActions {
    SendToOnPrem,
    SendToCloud,
    ListErrorDetails,
    MoveToPendingState
}

type OrderRequestListingActionsProps = ListActionButtonProps<OrderRequestListingItem>;

export const OrderRequestListingActions: React.FC<OrderRequestListingActionsProps> = ({
    item
}: OrderRequestListingActionsProps) => {
    const [state, setState] = useState<OrderRequestActions>();

    const { handleRefreshOrderRequest } = useRefreshOrderRequest(item);

    const options: CommandBarButtonMenuItem[] = [
        {
            text: "Cloud Processing",
            disabled: isSendToCloudDisabled(item),
            onClick: () => setState(OrderRequestActions.SendToCloud)
        },
        {
            text: "On-Prem Processing",
            disabled: isSendToOnPremDisabled(item),
            onClick: () => setState(OrderRequestActions.SendToOnPrem)
        },
        {
            text: "List error details",
            onClick: () => setState(OrderRequestActions.ListErrorDetails),
            disabled: isListOrderRequestErrorDetailsDisabled(item)
        },
        {
            text: "Refresh",
            onClick: handleRefreshOrderRequest,
            disabled: isRefreshActionDisabled(item)
        },
        {
            text: "Move request to Pending",
            disabled: isMoveToPendingStateDisabled(item),
            onClick: () => setState(OrderRequestActions.MoveToPendingState)
        }
    ];

    return (
        <AuthorizationWrapper accessRights={[SprintAccessRight.OrdAdmin]}>
            <TooltipHost content="Actions">
                <CommandBarButton icon="MoreVertical" menuItems={options} />
                <StatusDialogManager status={state} item={item} onClose={() => setState(undefined)} />
            </TooltipHost>
        </AuthorizationWrapper>
    );
};

const StatusDialogManager = ({
    status,
    item,
    onClose
}: {
    status?: OrderRequestActions;
    item: OrderRequestListingItem;
    onClose: () => void;
}) => {
    switch (status) {
        case OrderRequestActions.SendToOnPrem:
            return <SendOrderRequestToOnPremDialog isOpen item={item} onClose={onClose} />;
        case OrderRequestActions.SendToCloud:
            return <SendOrderRequestToCloudDialog isOpen item={item} onClose={onClose} />;
        case OrderRequestActions.ListErrorDetails:
            return <ListOrderRequestErrorDetails isOpen item={item} onClose={onClose} />;
        case OrderRequestActions.MoveToPendingState:
            return <MoveOrderRequestToPendingDialog isOpen item={item} onClose={onClose} />;
        default:
            return <></>;
    }
};

const isSendToOnPremDisabled = (item: OrderRequestListingItem): boolean => item.status !== OrderRequestStatus.Pending;
const isSendToCloudDisabled = (item: OrderRequestListingItem): boolean => item.status !== OrderRequestStatus.Pending;
const isRefreshActionDisabled = (item: OrderRequestListingItem): boolean => item.status !== OrderRequestStatus.Invalid;
const isListOrderRequestErrorDetailsDisabled = (item: OrderRequestListingItem): boolean =>
    item.status !== OrderRequestStatus.Invalid;
const isMoveToPendingStateDisabled = (item: OrderRequestListingItem): boolean =>
    item.status !== OrderRequestStatus.CloudProcessing && item.status !== OrderRequestStatus.OnPremProcessing;
